import React, { useState, useEffect, useCallback, useContext } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { LangContext } from '../App';
import api from '../services/api';

// ── Translations ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Statistiques par région',
    sousTitre: 'Observations fauniques actives agrégées par province canadienne.',
    filtres: 'Filtres',
    province: 'Province',
    toutes: 'Toutes les provinces',
    category: 'Catégorie',
    toutesCategories: 'Toutes les catégories',
    species: 'Espèce',
    especePlaceholder: 'ex. orignal, canards…',
    depuis: 'Depuis',
    appliquer: 'Appliquer',
    reinitialiser: 'Réinitialiser',
    chargement: 'Chargement…',
    erreur: 'Impossible de charger les statistiques.',
    totalGlobal: 'Total observations actives',
    repartition: 'Répartition par catégorie',
    topEspeces: 'Top espèces (toutes régions)',
    parRegion: 'Par province / territoire',
    aucuneDonnee: 'Aucune donnée pour cette sélection.',
    categories: {
      migrateur: 'Migrateurs',
      sedentaire: 'Sédentaires',
      spot_peche: 'Spots pêche',
      etat_zone: 'États de zone',
    },
    provinces: {
      QC: 'Québec',
      ON: 'Ontario',
      AB: 'Alberta',
      BC: 'Colombie-Britannique',
      MB: 'Manitoba',
      NB: 'Nouveau-Brunswick',
      NL: 'Terre-Neuve',
      NS: 'Nouvelle-Écosse',
      SK: 'Saskatchewan',
      PE: 'Î.-P.-É.',
      YT: 'Yukon',
      NT: 'T.N.-O.',
      NU: 'Nunavut',
    },
    observations: 'obs.',
    topRegionEspeces: 'Top espèces',
  },
  en: {
    title: 'Regional Statistics',
    sousTitre: 'Active wildlife observations aggregated by Canadian province.',
    filtres: 'Filters',
    province: 'Province',
    toutes: 'All provinces',
    category: 'Category',
    toutesCategories: 'All categories',
    species: 'Species',
    especePlaceholder: 'e.g. moose, ducks…',
    depuis: 'Since',
    appliquer: 'Apply',
    reinitialiser: 'Reset',
    chargement: 'Loading…',
    erreur: 'Failed to load statistics.',
    totalGlobal: 'Total active observations',
    repartition: 'Breakdown by category',
    topEspeces: 'Top species (all regions)',
    parRegion: 'By province / territory',
    aucuneDonnee: 'No data for this selection.',
    categories: {
      migrateur: 'Migratory',
      sedentaire: 'Resident',
      spot_peche: 'Fishing spots',
      etat_zone: 'Zone status',
    },
    provinces: {
      QC: 'Quebec',
      ON: 'Ontario',
      AB: 'Alberta',
      BC: 'British Columbia',
      MB: 'Manitoba',
      NB: 'New Brunswick',
      NL: 'Newfoundland',
      NS: 'Nova Scotia',
      SK: 'Saskatchewan',
      PE: 'P.E.I.',
      YT: 'Yukon',
      NT: 'N.W.T.',
      NU: 'Nunavut',
    },
    observations: 'obs.',
    topRegionEspeces: 'Top species',
  },
};

// ── Constants ──────────────────────────────────────────────────────────────────

const PROVINCES = ['QC', 'ON', 'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'SK', 'PE', 'YT', 'NT', 'NU'];
const CATEGORIES = ['migrateur', 'sedentaire', 'spot_peche', 'etat_zone'];
const CAT_COLORS = {
  migrateur: '#2563eb',
  sedentaire: '#16a34a',
  spot_peche: '#0891b2',
  etat_zone: '#d97706',
};

// ── Helpers ────────────────────────────────────────────────────────────────────

function Bar({ value, max, color }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div
      style={{
        background: 'var(--couleur-fond)',
        borderRadius: 4,
        overflow: 'hidden',
        height: 10,
        flex: 1,
      }}
    >
      <div
        style={{ width: `${pct}%`, background: color, height: '100%', transition: 'width .3s' }}
      />
    </div>
  );
}

// ── Component ──────────────────────────────────────────────────────────────────

export default function RegionStats() {
  const { lang } = useContext(LangContext);
  const t = T[lang] || T.fr;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Filters
  const [province, setProvince] = useState('');
  const [category, setCategorie] = useState('');
  const [species, setEspece] = useState('');
  const [depuis, setDepuis] = useState('');

  const load = useCallback(
    async (filters = {}) => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams();
        if (filters.province) params.set('province', filters.province);
        if (filters.category) params.set('category', filters.category);
        if (filters.species) params.set('species', filters.species);
        if (filters.depuis) params.set('depuis', filters.depuis);
        const qs = params.toString();
        const { data: d } = await api.get(`/stats/region${qs ? `?${qs}` : ''}`);
        setData(d);
      } catch {
        setError(t.erreur);
      } finally {
        setLoading(false);
      }
    },
    [t.erreur]
  );

  useEffect(() => {
    load({});
  }, [load]);

  const handleApply = () => load({ province, category, species, depuis });
  const handleReset = () => {
    setProvince('');
    setCategorie('');
    setEspece('');
    setDepuis('');
    load({});
  };

  const maxRegionTotal = data ? Math.max(...data.by_region.map((r) => r.total), 1) : 1;
  const maxCatTotal = data ? Math.max(...CATEGORIES.map((c) => data.by_category[c] || 0), 1) : 1;

  return (
    <>
      <Header />
      <main style={{ maxWidth: 900, margin: '0 auto', padding: '2rem 1rem' }}>
        {/* Title */}
        <h1 style={{ color: 'var(--couleur-or)', marginBottom: '.25rem' }}>{t.title}</h1>
        <p style={{ color: 'var(--couleur-texte-attenué)', marginBottom: '2rem' }}>{t.sousTitre}</p>

        {/* Filters */}
        <section
          style={{
            background: 'var(--couleur-fond)',
            borderRadius: 'var(--rayon)',
            padding: '1.25rem',
            marginBottom: '2rem',
          }}
        >
          <h2 style={{ fontSize: '1rem', marginBottom: '1rem', color: 'var(--couleur-or)' }}>
            {t.filtres}
          </h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
              gap: '.75rem',
              alignItems: 'end',
            }}
          >
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}
            >
              {t.province}
              <select
                value={province}
                onChange={(e) => setProvince(e.target.value)}
                style={{
                  padding: '.45rem .6rem',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: '#1a1a1a',
                  color: 'var(--couleur-texte)',
                }}
              >
                <option value="">{t.toutes}</option>
                {PROVINCES.map((p) => (
                  <option key={p} value={p}>
                    {t.provinces[p] || p}
                  </option>
                ))}
              </select>
            </label>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}
            >
              {t.category}
              <select
                value={category}
                onChange={(e) => setCategorie(e.target.value)}
                style={{
                  padding: '.45rem .6rem',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: '#1a1a1a',
                  color: 'var(--couleur-texte)',
                }}
              >
                <option value="">{t.toutesCategories}</option>
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {t.categories[c]}
                  </option>
                ))}
              </select>
            </label>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}
            >
              {t.species}
              <input
                value={species}
                onChange={(e) => setEspece(e.target.value)}
                placeholder={t.especePlaceholder}
                style={{
                  padding: '.45rem .6rem',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: '#1a1a1a',
                  color: 'var(--couleur-texte)',
                }}
              />
            </label>
            <label
              style={{ display: 'flex', flexDirection: 'column', gap: '.3rem', fontSize: '.85rem' }}
            >
              {t.depuis}
              <input
                type="date"
                value={depuis}
                onChange={(e) => setDepuis(e.target.value)}
                style={{
                  padding: '.45rem .6rem',
                  borderRadius: 6,
                  border: '1px solid #444',
                  background: '#1a1a1a',
                  color: 'var(--couleur-texte)',
                }}
              />
            </label>
            <div style={{ display: 'flex', gap: '.5rem', alignItems: 'flex-end' }}>
              <button
                onClick={handleApply}
                style={{
                  padding: '.5rem 1.1rem',
                  background: 'var(--couleur-or)',
                  color: '#000',
                  border: 'none',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                {t.appliquer}
              </button>
              <button
                onClick={handleReset}
                style={{
                  padding: '.5rem .9rem',
                  background: 'transparent',
                  color: 'var(--couleur-texte-attenué)',
                  border: '1px solid #444',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >
                {t.reinitialiser}
              </button>
            </div>
          </div>
        </section>

        {/* States */}
        {loading && <p style={{ color: 'var(--couleur-texte-attenué)' }}>{t.chargement}</p>}
        {error && <p style={{ color: 'var(--couleur-texte-erreur)' }}>{error}</p>}

        {data && !loading && (
          <>
            {/* Global total */}
            <div style={{ textAlign: 'center', margin: '1.5rem 0' }}>
              <span style={{ fontSize: '3rem', fontWeight: 700, color: 'var(--couleur-or)' }}>
                {data.total.toLocaleString()}
              </span>
              <p style={{ color: 'var(--couleur-texte-attenué)', marginTop: '.25rem' }}>
                {t.totalGlobal}
              </p>
            </div>

            {/* Category breakdown */}
            <section
              style={{
                background: 'var(--couleur-fond)',
                borderRadius: 'var(--rayon)',
                padding: '1.25rem',
                marginBottom: '1.5rem',
              }}
            >
              <h2 style={{ fontSize: '1rem', color: 'var(--couleur-or)', marginBottom: '1rem' }}>
                {t.repartition}
              </h2>
              {CATEGORIES.map((cat) => {
                const count = data.by_category[cat] || 0;
                return (
                  <div key={cat} style={{ marginBottom: '.8rem' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        fontSize: '.85rem',
                        marginBottom: '.25rem',
                      }}
                    >
                      <span style={{ display: 'flex', alignItems: 'center', gap: '.4rem' }}>
                        <span
                          style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: CAT_COLORS[cat],
                          }}
                        />
                        {t.categories[cat]}
                      </span>
                      <span style={{ color: 'var(--couleur-texte-attenué)' }}>
                        {count.toLocaleString()} {t.observations}
                      </span>
                    </div>
                    <Bar value={count} max={maxCatTotal} color={CAT_COLORS[cat]} />
                  </div>
                );
              })}
            </section>

            {/* Global top species */}
            {data.top_species.length > 0 && (
              <section
                style={{
                  background: 'var(--couleur-fond)',
                  borderRadius: 'var(--rayon)',
                  padding: '1.25rem',
                  marginBottom: '1.5rem',
                }}
              >
                <h2 style={{ fontSize: '1rem', color: 'var(--couleur-or)', marginBottom: '1rem' }}>
                  {t.topEspeces}
                </h2>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '.5rem' }}>
                  {data.top_species.map((s, i) => (
                    <span
                      key={s.species}
                      style={{
                        padding: '.3rem .75rem',
                        borderRadius: 20,
                        background: i === 0 ? 'var(--couleur-or)' : '#2a2a2a',
                        color: i === 0 ? '#000' : 'var(--couleur-texte)',
                        fontSize: '.85rem',
                        fontWeight: i === 0 ? 700 : 400,
                      }}
                    >
                      {s.species} <span style={{ opacity: 0.7 }}>({s.count})</span>
                    </span>
                  ))}
                </div>
              </section>
            )}

            {/* Per-region table */}
            <section
              style={{
                background: 'var(--couleur-fond)',
                borderRadius: 'var(--rayon)',
                padding: '1.25rem',
              }}
            >
              <h2 style={{ fontSize: '1rem', color: 'var(--couleur-or)', marginBottom: '1rem' }}>
                {t.parRegion}
              </h2>
              {data.by_region.length === 0 ? (
                <p style={{ color: 'var(--couleur-texte-attenué)' }}>{t.aucuneDonnee}</p>
              ) : (
                data.by_region.map((region) => (
                  <div
                    key={region.province}
                    style={{
                      marginBottom: '1.5rem',
                      paddingBottom: '1.5rem',
                      borderBottom: '1px solid #2a2a2a',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '1rem',
                        marginBottom: '.75rem',
                      }}
                    >
                      <span
                        style={{
                          background: 'var(--couleur-or)',
                          color: '#000',
                          fontWeight: 700,
                          borderRadius: 6,
                          padding: '.25rem .6rem',
                          fontSize: '.85rem',
                          minWidth: 36,
                          textAlign: 'center',
                        }}
                      >
                        {region.province}
                      </span>
                      <span style={{ fontWeight: 600 }}>
                        {t.provinces[region.province] || region.province}
                      </span>
                      <span
                        style={{
                          marginLeft: 'auto',
                          color: 'var(--couleur-texte-attenué)',
                          fontSize: '.9rem',
                        }}
                      >
                        {region.total.toLocaleString()} {t.observations}
                      </span>
                    </div>
                    {/* Region bar */}
                    <div style={{ marginBottom: '.75rem' }}>
                      <Bar value={region.total} max={maxRegionTotal} color="var(--couleur-or)" />
                    </div>
                    {/* Category breakdown */}
                    <div
                      style={{
                        display: 'flex',
                        gap: '.5rem',
                        flexWrap: 'wrap',
                        marginBottom: '.5rem',
                      }}
                    >
                      {CATEGORIES.filter((c) => region.by_category[c] > 0).map((c) => (
                        <span
                          key={c}
                          style={{
                            fontSize: '.78rem',
                            padding: '.2rem .55rem',
                            borderRadius: 12,
                            background: CAT_COLORS[c] + '22',
                            color: CAT_COLORS[c],
                            border: `1px solid ${CAT_COLORS[c]}44`,
                          }}
                        >
                          {t.categories[c]}: {region.by_category[c]}
                        </span>
                      ))}
                    </div>
                    {/* Top species */}
                    {region.top_species.length > 0 && (
                      <p
                        style={{
                          fontSize: '.8rem',
                          color: 'var(--couleur-texte-attenué)',
                          margin: 0,
                        }}
                      >
                        {t.topRegionEspeces}:{' '}
                        {region.top_species.map((s) => `${s.species} (${s.count})`).join(', ')}
                      </p>
                    )}
                  </div>
                ))
              )}
            </section>
          </>
        )}

        <div style={{ marginTop: '2rem' }}>
          <Link
            to="/"
            style={{ color: 'var(--couleur-or)', textDecoration: 'none', fontSize: '.9rem' }}
          >
            ← {lang === 'en' ? 'Home' : 'Accueil'}
          </Link>
        </div>
      </main>
    </>
  );
}
